import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { MessageSquare, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useTopics } from "@/hooks/useTopics";
import { useCategoriesByActivity } from "@/hooks/useCategoriesByActivity";
import { useForumSettings } from "@/hooks/useForumSettings";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { TopicTable } from "./TopicTable";
import { ReportModal } from "./ReportModal";
import { BannerAd } from "../ads/BannerAd";
import { AdSlot300x250 } from "../ads/AdSlot300x250";
import GPTResponsiveAd from "../ads/GPTResponsiveAd";
import { getCategoryDescription } from "@/utils/categoryCopy";

export const ForumHome = () => {
  const { getSetting } = useForumSettings();
  const [reportModal, setReportModal] = useState<{
    isOpen: boolean;
    topicId?: string;
  }>({
    isOpen: false,
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Pagination state for new topics
  const [newPage, setNewPage] = useState(1);

  // Paginated data hooks - only new topics
  const { data: newTopicsData, isLoading: newTopicsLoading } = useTopics(
    undefined,
    newPage,
    10,
    "last_reply_at"
  );

  const { data: level1Forums } = useCategoriesByActivity(null, 1); // Only Level 1 forums, sorted by activity
  const { data: level2Forums } = useCategoriesByActivity(undefined, 2); // Province/State forums, sorted by activity

  const handleReport = (topicId: string) => {
    setReportModal({
      isOpen: true,
      topicId,
    });
  };

  return (
    <div className="forum-spacing relative w-full overflow-x-hidden max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {getSetting("forum_name", "Minor Baseball Talks")} - Youth Baseball Forum
        </h1>
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <div className="text-muted-foreground space-y-3">
            <p>
              Welcome to Minor Baseball Talks, the premier youth baseball forum for players, parents, and coaches across the U.S. and Canada. From Little League and travel baseball to high school recruiting and player development, our community covers every level of the game.
            </p>
            <CollapsibleContent className="space-y-3">
              <p>
                Join thousands of passionate baseball fans to discuss teams, tournaments, training tips, gear reviews, scouting, and college recruitment. Whether you're a parent tracking your child's progress, a coach sharing drills, or a fan following the latest youth baseball news, this is your place to connect, learn, and grow the game.
              </p>
              <p>
                At Minor Baseball Talks, we bring together the heart of youth baseball—community, competition, and conversation. Explore team forums by state, age group, and league. Share game results, highlight players, or ask for advice on hitting, pitching, and coaching.
              </p>
              <p>
                Start the conversation today and be part of a growing online hub that celebrates the future stars of baseball.
              </p>
            </CollapsibleContent>
            <CollapsibleTrigger className="flex items-center gap-1 text-sm text-primary hover:underline transition-all">
              {isExpanded ? "Read less about this youth baseball forum" : "Read more about this youth baseball forum"}
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
            </CollapsibleTrigger>
          </div>
        </Collapsible>
      </div>

      {/* Leaderboard Top */}
      <GPTResponsiveAd slotId="responsive-1" />

      {/* New Topics Section */}
      <div className="forum-spacing">
        <div>
          <h2 className="text-lg font-bold text-foreground">Latest Youth Baseball Discussions</h2>
          <p className="text-sm text-muted-foreground mt-1">
            New questions, advice, team updates, and tournament talk from baseball families and coaches.
          </p>
        </div>
        <TopicTable
          topics={
            newTopicsData?.data?.map((topic) => ({
              ...topic,
              username: topic.profiles?.username || null,
              avatar_url: topic.profiles?.avatar_url || null,
              category_name: topic.categories?.name || "General",
              category_color: topic.categories?.color || "#3b82f6",
              category_slug: topic.categories?.slug || "",
              slug: topic.slug,
              hot_score: 0,
              last_post_id: topic.last_post_id,
              parent_category_id: topic.categories?.parent_category_id || null,
              parent_category_slug: topic.categories?.parent_category?.slug || null,
              last_reply_username: topic.last_reply_username,
              last_reply_avatar: topic.last_reply_avatar,
            })) || []
          }
          loading={newTopicsLoading}
          showCategory={true}
        />
        {newTopicsData && newTopicsData.data.length > 0 && (
          <PaginationControls
            currentPage={newPage}
            totalPages={newTopicsData.totalPages}
            totalItems={newTopicsData.totalCount}
            itemsPerPage={10}
            onPageChange={setNewPage}
            loading={newTopicsLoading}
          />
        )}
      </div>

      {/* Ad Slot 300x250 */}
      <AdSlot300x250 slotId="300x250-4" />

      {/* Forums Section */}
      <div className="forum-spacing">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground">
            Browse Main Forums
          </h2>
        </div>

        <div
          className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
          style={{ minHeight: "200px" }}
        >
          {level1Forums?.map((forum) => (
            <Link
              key={forum.id}
              to={`/category/${forum.slug}`}
              className="block h-full"
            >
              <Card
                className="p-3 hover:bg-forum-row-hover transition-colors cursor-pointer border border-forum-border-subtle h-[120px] flex flex-col"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: forum.color }}
                    />
                    <h3 className="font-medium text-foreground group-hover:text-primary transition-colors text-sm line-clamp-1">
                      {forum.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
                    <span className="font-semibold">{forum.topic_count || 0}</span>
                    <MessageSquare className="h-3 w-3" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2 flex-1">
                  {getCategoryDescription(forum)}
                </p>
              </Card>
            </Link>
          ))}
        </div>

        {(!level1Forums || level1Forums.length === 0) && (
          <Card className="p-6 text-center">
            <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-base font-semibold mb-2">
              No forums available
            </h3>
            <p className="text-muted-foreground text-sm">
              Forums will appear here once they are created.
            </p>
          </Card>
        )}
      </div>

      {/* Ad Slot 300x250 */}
      <AdSlot300x250 slotId="300x250-5" />

      {/* Province/State Forums Section */}
      <div className="forum-spacing">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground">
            Browse Province / State Forums
          </h2>
        </div>

        {level2Forums && level2Forums.length > 0 ? (
          <div className="forum-spacing">
            {(() => {
              // Find USA and Canada parent categories from level1Forums
              const usaParent = level1Forums?.find(cat => cat.slug === 'usa');
              const canadaParent = level1Forums?.find(cat => cat.slug === 'canada');

              // Group by region using parent_category_id
              const canadianForums = level2Forums
                .filter(forum => canadaParent && forum.parent_category_id === canadaParent.id)
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

              const usaForums = level2Forums
                .filter(forum => usaParent && forum.parent_category_id === usaParent.id)
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

              const countries = [];
              if (canadianForums.length > 0) {
                countries.push({ name: "Canada", forums: canadianForums });
              }
              if (usaForums.length > 0) {
                countries.push({ name: "USA", forums: usaForums });
              }

              return countries.map((country, index) => (
                <div key={country.name}>
                  <div className="forum-spacing">
                    <h3 className="text-base font-semibold text-foreground border-b pb-1 forum-header">
                      {country.name}
                    </h3>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {country.forums.map((forum) => (
                        <Link
                          key={forum.id}
                          to={`/category/${forum.slug}`}
                          className="block h-full"
                        >
                          <Card className="p-3 hover:shadow-md transition-shadow cursor-pointer h-[120px] flex flex-col">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <div
                                  className="w-3 h-3 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: forum.color }}
                                />
                                <h4 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors line-clamp-1">
                                  {forum.name}
                                </h4>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
                                <span className="font-semibold">{forum.topic_count || 0}</span>
                                <MessageSquare className="h-3 w-3" />
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2 flex-1">
                              {getCategoryDescription(forum)}
                            </p>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                  {index === 0 && <AdSlot300x250 slotId="300x250-6" />}
                </div>
              ));
            })()}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              No province/state forums available
            </h3>
            <p className="text-muted-foreground">
              Province and state forums will appear here once they are created.
            </p>
          </Card>
        )}
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModal.isOpen}
        onClose={() => setReportModal({ isOpen: false })}
        topicId={reportModal.topicId}
        contentType="topic"
      />
    </div>
  );
};
